import type { ApiClient } from './client'
import type { components } from '@aurigaengineering/contracts-admin'

export type Exam = components['schemas']['ExamView']
export type Sitting = components['schemas']['SittingView']
/** A Sitting's dates: everything about it but its name. */
export type SittingDates = components['schemas']['SittingBody']
/** One of the Exam's own Components, from its Format. */
export type ExamPaper = components['schemas']['ComponentFormatView']
/** A Topic as a write takes it: its Subtopics are their texts, in the specification's order. */
export type Topic = components['schemas']['TopicBody']
/** A Topic read back: its Label, and each Subtopic with the code the API derived for it. */
export type ExamTopic = components['schemas']['ExamTopicView']
/**
 * The Exam's Topics and the Topics each Session examines, as a write takes them: every Paper by
 * its Style, and its Sections -- or, for a Paper the Exam does not split, its Topic Codes directly.
 */
export type TopicsDocument = components['schemas']['SetTopics']
/** The same read back, each Paper with the Format's name, length and Question count alongside. */
export type TopicsView = components['schemas']['TopicsView']

/**
 * The Exam catalogue, as the Exam screens use it.
 *
 * The screens talk to this rather than to the typed client, for one reason: a test can hand
 * it a stand-in. Everything below is the contract's own shape -- these are the generated
 * types, not a second set written by hand -- so what a test exercises is the screen's
 * behaviour and not a fiction about the wire.
 */
export interface ExamsApi {
  exportStatistics(exam: string): Promise<components['schemas']['ExamStatisticsExport']>
  /** Every Exam, with its Format and its Sittings. */
  list(): Promise<Exam[]>
  setAvailability(exam: string, enabled: boolean): Promise<Exam>
  addSitting(exam: string, name: string, sitting: SittingDates): Promise<Exam>
  /** A Sitting's dates, written whole. The name is its address and stays. */
  changeSitting(exam: string, name: string, sitting: SittingDates): Promise<Exam>
  /** One Exam's Topics, their Subtopics, and the Topics each Session examines. */
  topics(exam: string): Promise<TopicsView>
  /** Writes the whole document: every Topic and the Topics every Session examines (app:R83). */
  setTopics(exam: string, document: TopicsDocument): Promise<TopicsView>
}

export function examsApi(client: ApiClient): ExamsApi {
  return {
    async exportStatistics(exam) {
      const { data, error } = await client.GET('/admin/exams/{code}/statistics-export', { params: { path: { code: exam } } })
      return data ?? failed(error)
    },
    async setAvailability(exam, enabled) {
      const { data, error } = await client.PUT('/admin/exams/{code}/availability', { params: { path: { code: exam } }, body: { enabled } })
      return data ?? failed(error)
    },
    async list() {
      const { data, error } = await client.GET('/admin/exams', {})
      return data ?? failed(error)
    },

    async addSitting(exam, name, sitting) {
      const { data, error } = await client.POST('/admin/exams/{code}/sittings', {
        params: { path: { code: exam } },
        body: { name, ...sitting },
      })
      return data ?? failed(error)
    },

    async changeSitting(exam, name, sitting) {
      const { data, error } = await client.PUT('/admin/exams/{code}/sittings/{sitting}', {
        params: { path: { code: exam, sitting: name } },
        body: sitting,
      })
      return data ?? failed(error)
    },

    async topics(exam) {
      const { data, error } = await client.GET('/admin/exams/{code}/topics', {
        params: { path: { code: exam } },
      })
      return data ?? failed(error)
    },

    async setTopics(exam, document) {
      const { data, error } = await client.PUT('/admin/exams/{code}/topics', {
        params: { path: { code: exam } },
        body: document,
      })
      return data ?? failed(error)
    },
  }
}

/**
 * Turns whatever the API refused with into an Error the screen can show.
 *
 * ProblemDetails where the API sent one -- its `detail` is written to be read by the person
 * who caused it -- and the status alone where it did not, which is what a 401 or a 403 from
 * the pipeline looks like.
 */
function failed(error: unknown): never {
  const problem = error as { detail?: string; title?: string } | undefined

  throw new Error(problem?.detail ?? problem?.title ?? 'The API refused that.')
}
